import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "@store/middleware";

import { getApplicationSpaBackendUrl } from "@utils";
import { httpHeaders } from "@shared/constants/httpHeaders";
import {
  type IBifrostAvailabilityQuery,
  type IBifrostAvailabilityResponse,
  type IBifrostBooking,
  type IBifrostBookingAvailabilityQuery,
  type IBifrostBookingCreateInput,
  type IBifrostBookingProductsQuery,
  type IBifrostBookingProductsResponse,
  type IBifrostBookingResponse,
  type IBifrostBookingsResponse,
  type IBifrostBookingUpdateInput,
  type IBifrostBookingWithPreparationResponse,
} from "@types";

export const BIFROST_BOOKINGS_API_REDUCER_KEY = "bifrostBookingsApi";

const withBookingDateTimes = <T extends IBifrostBooking>(booking: T): T => ({
  ...booking,
  from: booking.from_date == null || booking.from_time == null ? "" : `${booking.from_date}T${booking.from_time}Z`,
  to: booking.to_date == null || booking.to_time == null ? "" : `${booking.to_date}T${booking.to_time}Z`,
});

const withAvailabilitySlotDateTimes = (response: IBifrostAvailabilityResponse): IBifrostAvailabilityResponse => ({
  ...response,
  data: {
    ...response.data,
    items: response.data.items.map((item) => ({
      ...item,
      time_slots: item.time_slots.map((slot) => ({
        ...slot,
        from: `${item.date}T${slot.from_time}Z`,
        to: `${item.date}T${slot.to_time}Z`,
      })),
    })),
  },
});

export const bifrostBookingsApi = createApi({
  reducerPath: BIFROST_BOOKINGS_API_REDUCER_KEY,
  tagTypes: ["BIFROST_BOOKINGS", "BIFROST_AVAILABILITY"],
  baseQuery: (arguments_, api, extraOptions) =>
    baseQueryWithAuth({ baseUrl: getApplicationSpaBackendUrl() })(arguments_, api, {
      ...extraOptions,
      isExternalService: true,
      isUnauthorizedLogoutEnabled: false,
    }),
  endpoints: (builder) => ({
    getBifrostBookings: builder.query<IBifrostBookingsResponse, void>({
      query: () => ({
        url: "/bookings",
        method: "GET",
      }),
      transformResponse: (response: IBifrostBookingsResponse): IBifrostBookingsResponse => ({
        ...response,
        data: (response.data ?? []).map((booking) => withBookingDateTimes(booking)),
      }),
      providesTags: ["BIFROST_BOOKINGS"],
    }),
    getBifrostBooking: builder.query<
      IBifrostBookingWithPreparationResponse,
      { id: IBifrostBooking["id"]; magicToken: string | null }
    >({
      query: ({ id, magicToken }) => ({
        url: `/bookings/${id}`,
        method: "GET",
        headers: magicToken == null ? undefined : { [httpHeaders.magicToken]: magicToken },
      }),
      transformResponse: (
        response: IBifrostBookingWithPreparationResponse,
      ): IBifrostBookingWithPreparationResponse => ({
        ...response,
        data: withBookingDateTimes(response.data),
      }),
      providesTags: ["BIFROST_BOOKINGS"],
    }),
    getBifrostBookingProducts: builder.query<IBifrostBookingProductsResponse, IBifrostBookingProductsQuery>({
      query: (parameters) => ({
        url: "/booking-products",
        method: "GET",
        params: parameters,
      }),
    }),
    getBifrostAvailability: builder.query<IBifrostAvailabilityResponse, IBifrostAvailabilityQuery>({
      query: (parameters) => ({
        url: "/availability",
        method: "GET",
        params: parameters,
      }),
      transformResponse: withAvailabilitySlotDateTimes,
      providesTags: ["BIFROST_AVAILABILITY"],
    }),
    getBifrostBookingAvailability: builder.query<
      IBifrostAvailabilityResponse,
      IBifrostBookingAvailabilityQuery & { magicToken: string | null }
    >({
      query: ({ id, magicToken, ...parameters }) => ({
        url: `/bookings/${id}/availability`,
        method: "GET",
        params: parameters,
        headers: magicToken == null ? undefined : { [httpHeaders.magicToken]: magicToken },
      }),
      transformResponse: withAvailabilitySlotDateTimes,
      providesTags: ["BIFROST_AVAILABILITY"],
    }),
    createBifrostBooking: builder.mutation<IBifrostBookingResponse, IBifrostBookingCreateInput>({
      query: (body) => ({
        url: "/bookings",
        method: "POST",
        body,
      }),
      transformResponse: (response: IBifrostBookingResponse): IBifrostBookingResponse => ({
        ...response,
        data: withBookingDateTimes(response.data),
      }),
      invalidatesTags: ["BIFROST_BOOKINGS", "BIFROST_AVAILABILITY"],
    }),
    updateBifrostBooking: builder.mutation<
      IBifrostBookingResponse,
      { id: IBifrostBooking["id"]; body: IBifrostBookingUpdateInput; magicToken: string | null }
    >({
      query: ({ id, body, magicToken }) => ({
        url: `/bookings/${id}`,
        method: "PATCH",
        body,
        headers: magicToken == null ? undefined : { [httpHeaders.magicToken]: magicToken },
      }),
      transformResponse: (response: IBifrostBookingResponse): IBifrostBookingResponse => ({
        ...response,
        data: withBookingDateTimes(response.data),
      }),
      invalidatesTags: ["BIFROST_BOOKINGS", "BIFROST_AVAILABILITY"],
    }),
  }),
});

export const {
  useGetBifrostBookingsQuery,
  useGetBifrostBookingQuery,
  useGetBifrostBookingProductsQuery,
  useGetBifrostAvailabilityQuery,
  useGetBifrostBookingAvailabilityQuery,
  useCreateBifrostBookingMutation,
  useUpdateBifrostBookingMutation,
} = bifrostBookingsApi;
